import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";

export const inputCls =
  "w-full border border-cream/15 bg-bark px-4 py-3 font-body text-sm font-light text-cream placeholder:text-cream/30 focus:border-brass/60 focus:outline-none transition-colors";

export const selectCls = inputCls + " appearance-none";

export const BtnPrimary = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`rounded-full border border-brass/70 px-6 py-2.5 font-body text-[10px] tracking-[0.3em] uppercase text-brass transition-all duration-300 hover:bg-brass hover:text-ink ${className}`}
  >
    {children}
  </button>
);

export const BtnGhost = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`rounded-full border border-cream/20 px-6 py-2.5 font-body text-[10px] tracking-[0.3em] uppercase text-cream/70 transition-all duration-300 hover:border-cream/60 hover:text-cream ${className}`}
  >
    {children}
  </button>
);

export const PageHeader = ({ title, mal, sub, children }) => (
  <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
    <div>
      <h1 className="font-display text-3xl tracking-tight text-ivory sm:text-4xl">
        {title} {mal && <span className="font-mal text-xl text-cream/40">{mal}</span>}
      </h1>
      {sub && <p className="mt-2 font-body text-sm font-light text-cream/55">{sub}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-4">{children}</div>
  </div>
);

export const SearchInput = ({ value, onChange, placeholder = "Search", className = "" }) => (
  <div className={`relative w-full max-w-sm md:w-64 ${className}`}>
    <Search size={14} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/35" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-cream/15 bg-bark py-2.5 pl-10 pr-4 font-body text-xs font-light text-cream placeholder:text-cream/30 focus:border-brass/60 focus:outline-none"
    />
  </div>
);

export const StatusPill = ({ tone = "brass", children }) => {
  const tones = {
    brass: "border-brass/50 text-brass",
    cream: "border-cream/25 text-cream/55",
    terra: "border-terra/60 text-terra",
    leaf: "border-leaf text-[#7d9b6e]",
  };
  return (
    <span className={`inline-block border px-3 py-1 font-body text-[9px] tracking-[0.25em] uppercase ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const TagChip = ({ children }) => (
  <span className="inline-block border border-cream/15 px-2.5 py-0.5 font-body text-[9px] tracking-[0.2em] uppercase text-cream/55">
    {children}
  </span>
);

export const Modal = ({ title, onClose, children, wide }) => (
  <AnimatePresence>
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className={`max-h-[90vh] w-full overflow-y-auto border border-cream/15 bg-[#140d08] p-8 ${wide ? "max-w-3xl" : "max-w-lg"}`}
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-8 flex items-start justify-between">
          <h2 className="font-display text-2xl tracking-tight text-ivory">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="text-cream/50 hover:text-cream">
            <X size={18} strokeWidth={1.25} />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

export const Confirm = ({ title, body, confirmLabel = "REMOVE", cancelLabel = "KEEP IT", onConfirm, onCancel, testId }) => (
  <AnimatePresence>
    <motion.div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-md border border-cream/15 bg-[#140d08] p-10 text-center"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <h2 className="font-display text-2xl tracking-tight text-ivory">{title}</h2>
        <p className="mt-4 font-body text-sm font-light leading-relaxed text-cream/60">{body}</p>
        <div className="mt-10 flex items-center justify-center gap-4">
          <BtnGhost data-testid={`${testId}-cancel`} onClick={onCancel}>{cancelLabel}</BtnGhost>
          <button
            data-testid={`${testId}-confirm`}
            onClick={onConfirm}
            className="rounded-full border border-terra/70 px-6 py-2.5 font-body text-[10px] tracking-[0.3em] uppercase text-terra transition-all duration-300 hover:bg-terra hover:text-ivory"
          >
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

export const EmptyState = ({ title, line, actionLabel, onAction }) => (
  <div className="border border-dashed border-cream/15 px-8 py-20 text-center">
    <p className="font-display text-2xl italic text-cream/70">{title}</p>
    <p className="mt-3 font-body text-sm font-light text-cream/50">{line}</p>
    {actionLabel && (
      <BtnPrimary className="mt-8" onClick={onAction}>
        {actionLabel}
      </BtnPrimary>
    )}
  </div>
);

export const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-cream/50">
      {label}
    </span>
    {children}
    {error && <span className="mt-2 block font-body text-[11px] text-terra">{error}</span>}
  </label>
);

export const TagPicker = ({ options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((t) => {
      const on = selected.includes(t.id);
      return (
        <button
          key={t.id}
          type="button"
          data-testid={`tag-pick-${t.id}`}
          onClick={() => onToggle(t.id)}
          className={`border px-3 py-1.5 font-body text-[10px] tracking-[0.2em] uppercase transition-all duration-300 ${
            on ? "border-brass bg-brass/10 text-brass" : "border-cream/15 text-cream/50 hover:border-cream/40"
          }`}
        >
          {t.name}
        </button>
      );
    })}
  </div>
);

export const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`relative h-5 w-10 rounded-full border transition-colors duration-300 ${
      checked ? "border-brass bg-brass/20" : "border-cream/25 bg-bark"
    }`}
  >
    <span
      className={`absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full transition-all duration-300 ${
        checked ? "left-5 bg-brass" : "left-1 bg-cream/40"
      }`}
    />
  </button>
);
